import React, { useState, useEffect } from 'react';
import { Typography, Paper, Link, Box, Tooltip, Snackbar, Alert, Table, TableBody, TableCell, TableContainer, TableRow, Accordion, AccordionSummary, AccordionDetails } from '@mui/material'; // Import Snackbar and Alert
import { Assignment, Flag, ExpandMore } from '@mui/icons-material'; // Import icons
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Carousel } from 'react-responsive-carousel'; // Import Carousel
import axios from 'axios'; // Import axios for API calls
import config from '../config/config'; // Import configuration
import { localeMap, formatDate } from '../utils/formatterUtils'; // Import localeMap and formatDate

const MilestoneGridView = ({ milestones, tasks, onDragEnd, handleDialogOpen, handleDeleteTask, handleSaveMilestone }) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [expandedMilestone, setExpandedMilestone] = useState(null);
  const [milestoneImages, setMilestoneImages] = useState({});

  const getStatusColor = (status) => {
    switch (status) {
      case 'In Progress':
        return 'orange';
      case 'Completed':
        return 'green';
      case 'Delayed':
        return 'lightcoral';
      default:
        return 'black';
    }
  };

  const handleSave = async () => {
    try {
      await handleSaveMilestone();
      setSnackbarMessage('Save successful!');
      setSnackbarSeverity('success');
    } catch (error) {
      setSnackbarMessage('Save failed!');
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const fetchImages = async (taskId, milestoneId) => {
    try {
      const response = await axios.get(`${config.apiBaseUrl}/api/tasks/${taskId}/images`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'X-TenantID': 'admin',
        },
      });
      const taskImages = response.data;

      // Download each image
      const downloadedImages = await Promise.all(taskImages.map(async (taskImage) => {
        const fileName = taskImage.imageUrl.split('/').pop(); // Extract the file name from the URL
        console.log(`Milestone ID: ${milestoneId}, Task ID: ${taskId}, Image Name: ${fileName}`); // Debug statement
        try {
          const downloadResponse = await axios.get(`${config.apiBaseUrl}/api/tasks/${taskId}/downloadImage/${fileName}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
              'X-TenantID': 'admin',
            },
            responseType: 'blob',
          });

          if (downloadResponse.status === 200) {
            const imageUrl = URL.createObjectURL(downloadResponse.data);
            return { ...taskImage, imageUrl };
          } else {
            console.error(`Failed to download image ${fileName} with status code: ${downloadResponse.status}`);
            return null;
          }
        } catch (downloadError) {
          console.error(`Error downloading image ${fileName}:`, downloadError);
          return null;
        }
      }));

      return downloadedImages.filter(image => image !== null);
    } catch (error) {
      console.error('Error fetching images:', error);
      if (error.response) {
        console.error('Error response:', error.response);
      }
      return [];
    }
  };

  const handleAccordionToggle = async (milestoneId) => {
    if (expandedMilestone === milestoneId) {
      setExpandedMilestone(null);
    } else {
      setExpandedMilestone(milestoneId);
      if (!milestoneImages[milestoneId] || milestoneImages[milestoneId].length === 0) {
        const milestoneTasks = tasks[milestoneId] || [];
        const allImages = await Promise.all(milestoneTasks.map(task => fetchImages(task.id, milestoneId)));
        setMilestoneImages((prevImages) => ({
          ...prevImages,
          [milestoneId]: allImages.flat(),
        }));
      }
    }
  };

  // Sort milestones by startDate
  const sortedMilestones = [...milestones].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

  return (
    <Box>
      <DragDropContext onDragEnd={onDragEnd}>
        <TableContainer>
          <Table>
            <TableBody>
              {sortedMilestones.map((milestone) => (
                <React.Fragment key={milestone.id}>
                  <TableRow>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Tooltip title="Milestone">
                          <Flag style={{ color: getStatusColor(milestone.status), marginRight: '8px' }} />
                        </Tooltip>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{milestone.name}</Typography>
                      </Box>
                    </TableCell>
                    {['In Progress', 'Completed', 'Delayed'].map((status) => (
                      <TableCell key={status} sx={{ verticalAlign: 'top', minWidth: '300px' }}>
                        <Droppable droppableId={`${status}-${milestone.id}`}>
                          {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps}>
                              <Paper
                                elevation={3}
                                sx={{ padding: '16px', borderColor: getStatusColor(status), borderWidth: '2px', borderStyle: 'solid', minHeight: '400px' }}
                              >
                                <Typography variant="h6" sx={{ color: getStatusColor(status), fontWeight: 'bold' }}>{status}</Typography>
                                {tasks[milestone.id] && tasks[milestone.id]
                                  .filter((task) => task.status === status)
                                  .sort((a, b) => new Date(a.startDate) - new Date(b.startDate)) // Sort tasks by start date
                                  .map((task, index) => (
                                    <Draggable key={task.id} draggableId={task.id.toString()} index={index}>
                                      {(provided, snapshot) => (
                                        <Paper
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          {...provided.dragHandleProps}
                                          sx={{
                                            padding: '8px',
                                            margin: '8px 0',
                                            backgroundColor: snapshot.isDragging ? 'lightblue' : 'white',
                                            borderColor: getStatusColor(task.status),
                                            borderWidth: '1px',
                                            borderStyle: 'solid',
                                            boxShadow: snapshot.isDragging ? '0 4px 8px rgba(0, 0, 0, 0.2)' : 'none',
                                            transform: snapshot.isDragging ? 'scale(0.98)' : 'none',
                                            transition: 'transform 0.2s, box-shadow 0.2s',
                                            '&:hover': {
                                              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                                            },
                                            fontSize: '0.875rem', // Reduced font size for task box
                                          }}
                                        >
                                          <Box display="flex" alignItems="center">
                                            <Tooltip title="Task">
                                              <Assignment style={{ color: getStatusColor(task.status), marginRight: '8px' }} />
                                            </Tooltip>
                                            <Typography sx={{ fontSize: '0.875rem' }}>{task.name}</Typography>
                                          </Box>
                                          <Typography sx={{ fontSize: '0.875rem' }}>Status: <span style={{ color: getStatusColor(task.status), fontWeight: 'bold' }}>{task.status}</span></Typography>
                                          <Typography sx={{ fontSize: '0.875rem' }}>Assigned To: {task.assignedTo}</Typography>
                                          <Typography sx={{ fontSize: '0.875rem' }}>Start Date: {formatDate(task.startDate, config.defaultLocale)}</Typography>
                                          <Typography sx={{ fontSize: '0.875rem' }}>End Date: {formatDate(task.endDate, config.defaultLocale)}</Typography>
                                          <Link component="button" variant="body2" onClick={() => handleDialogOpen(milestone, task)} sx={{ fontSize: '0.875rem' }}>
                                            Edit
                                          </Link>
                                          <Link component="button" variant="body2" onClick={() => handleDeleteTask(task.id, milestone.id)} sx={{ ml: 1, fontSize: '0.875rem' }}>
                                            Delete
                                          </Link>
                                        </Paper>
                                      )}
                                    </Draggable>
                                  ))}
                                {provided.placeholder}
                              </Paper>
                            </div>
                          )}
                        </Droppable>
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Accordion expanded={expandedMilestone === milestone.id} onChange={() => handleAccordionToggle(milestone.id)}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography>Images</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Carousel>
                            {milestoneImages[milestone.id] && milestoneImages[milestone.id].map((image, index) => (
                              <Box key={index} display="flex" justifyContent="center" alignItems="center">
                                <img src={image.imageUrl} alt={`Milestone ${milestone.id} Image ${index}`} style={{ maxHeight: '200px', maxWidth: '100%', objectFit: 'contain' }} />
                              </Box>
                            ))}
                          </Carousel>
                        </AccordionDetails>
                      </Accordion>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DragDropContext>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MilestoneGridView;